// Reference to the DobPicker class to use the event name constant

// An SSN sponsor's identity fields: [input, required message, invalid message, predicate].
const ssnIdentityFields = [
    [
        'registrationSsnLastFourInput',
        'ssnLastFourMsgRequired',
        'ssnLastFourMsgInvalid',
        isValidLastFourDigits
    ],
    [
        'registrationCardLastFourInput',
        'cardLastFourMsgRequired',
        'cardLastFourMsgInvalid',
        isValidLastFourDigits
    ],
    [
        'registrationZipcodeInput',
        'zipcodeMsgRequired',
        'zipcodeMsgInvalid',
        isFiveDigitZipcode
    ]
];

export const registrationState = {
    isDOBValid: false,
    isEmailValid: false,
    isConfirmEmailDirty: false,
    isUnderAge: false,

    getControls: function () {
        // inputs
        const registrationMemberIdInput = document.getElementById(
            'registrationMemberIdInput'
        );
        const registrationSsnLastFourInput = document.getElementById(
            'registrationSsnLastFourInput'
        );
        const registrationEmailInput = document.getElementById(
            'registrationEmailInput'
        );
        const registrationConfirmEmailInput = document.getElementById(
            'registrationConfirmEmailInput'
        );
        const registrationDOBPicker = document.getElementById('dobPicker');
        const registrationPhoneInput = document.getElementById(
            'registrationPhoneInput'
        );
        const registrationTermsCheckbox = document.getElementById(
            'registrationTermsCheckbox'
        );
        const registrationSubmitButton = document.getElementById(
            'registrationSubmitButton'
        );
        const ageGateContainer = document.getElementById('ageGateContainer');
        const ageGateCheckbox = document.getElementById('ageGate');

        // feedback messages
        const memberIdMsgIdRequired = document.getElementById(
            'memberIdMsgIdRequired'
        );
        const memberIdMsgIdInvalid = document.getElementById(
            'memberIdMsgIdInvalid'
        );
        const emailRequired = document.getElementById('emailRequired');
        const emailInvalid = document.getElementById('emailInvalid');
        const emailsDoNotMatch = document.getElementById('emailsDoNotMatch');
        const dobInvalid = document.getElementById('dobInvalid');
        const phoneInvalid = document.getElementById('phoneInvalid');
        const termsFeedbackMessage = document.getElementById(
            'termsFeedbackMessage'
        );

        // group
        const confirmationEmailGroup = document.getElementById(
            'confirmationEmailGroup'
        );

        return {
            registrationMemberIdInput,
            registrationSsnLastFourInput,
            registrationEmailInput,
            registrationConfirmEmailInput,
            registrationDOBPicker,
            registrationPhoneInput,
            registrationTermsCheckbox,
            registrationSubmitButton,
            memberIdMsgIdRequired,
            memberIdMsgIdInvalid,
            emailRequired,
            emailInvalid,
            emailsDoNotMatch,
            dobInvalid,
            phoneInvalid,
            termsFeedbackMessage,
            confirmationEmailGroup,
            ageGateContainer,
            ageGateCheckbox
        };
    },

    init: function () {
        // Once per form element: afterSettle also fires for unrelated swaps like
        // the result modal, while a failed submit swaps in a fresh form whose
        // inputs need binding.
        const form = document.getElementById('registrationForm');
        if (form.dataset.validationBound) return;
        form.dataset.validationBound = 'true';

        const {
            registrationMemberIdInput,
            registrationSsnLastFourInput,
            registrationEmailInput,
            registrationConfirmEmailInput,
            registrationDOBPicker,
            registrationPhoneInput,
            registrationTermsCheckbox
        } = this.getControls();

        registrationDOBPicker.addEventListener(
            window.DobPicker.ValidityChangeEventName,
            validationChangedCustomeEvent => {
                this.isDOBValid = validationChangedCustomeEvent.detail.isValid;
                this.validateAllInputs();
            }
        );

        // Listen for the dobComplete event to check age
        registrationDOBPicker.addEventListener(
            window.DobPicker.DobCompleteEventName,
            event => {
                this.checkAgeGate();
            }
        );

        // check if all dob inputs are valid because the event handler probably won't be connected before this init method is called
        const dobInputs = registrationDOBPicker.getElementsByTagName('input');
        this.isDOBValid = true;
        for (const dobInput of dobInputs) {
            this.isDOBValid = this.isDOBValid && dobInput.validity.valid;
        }

        // Check age gate on initial load if DOB is already filled
        if (this.isDOBValid) {
            this.checkAgeGate();
        }

        // event listeners for inputs
        if (registrationMemberIdInput) {
            registrationMemberIdInput.addEventListener('blur', event => {
                this.validateMemberId();
                this.validateAllInputs();
            });
        }

        // An SSN sponsor's form renders all three of these together.
        if (registrationSsnLastFourInput) {
            for (const [
                inputId,
                requiredId,
                invalidId,
                isValid
            ] of ssnIdentityFields) {
                const input = document.getElementById(inputId);
                // 'input' re-gates submit as digits arrive; errors wait for blur.
                input.addEventListener('input', () => this.validateAllInputs());
                input.addEventListener('blur', () => {
                    showFieldFeedback(
                        input,
                        document.getElementById(requiredId),
                        document.getElementById(invalidId),
                        isValid
                    );
                    this.validateAllInputs();
                });
            }
        }

        registrationEmailInput.addEventListener('input', event => {
            this.validateEmail();
            // if confirmEmail is touched, validate
            if (this.isConfirmEmailDirty) {
                this.validateTwoEmailsMatch();
            }
            this.validateAllInputs();
        });

        // Use 'input' rather than 'keyup' so pasting and autofill are
        // covered: mobile paste (long-press → Paste) never emits keyup, which
        // left the submit button disabled after a valid paste.
        registrationConfirmEmailInput.addEventListener('input', event => {
            this.isConfirmEmailDirty = true;
            this.validateTwoEmailsMatch();
            this.validateAllInputs();
        });

        registrationPhoneInput.addEventListener('input', event => {
            this.validatePhoneNumber();
            this.validateAllInputs();
        });

        registrationTermsCheckbox.addEventListener('change', event => {
            this.validateAllInputs();
        });

        // Add event listener for the ageGate checkbox
        const { ageGateCheckbox } = this.getControls();
        if (ageGateCheckbox) {
            ageGateCheckbox.addEventListener('change', event => {
                this.validateAllInputs();
            });
        }

        window.attachMinorVerificationToForm =
            this.attachMinorVerificationToForm.bind(this);

        // A form swapped in after a failed submit arrives filled in with a
        // disabled button, and the DOB picker's first validity event fired
        // before these listeners existed.
        this.validateAllInputs();
    },

    attachMinorVerificationToForm: function (formId) {
        const { ageGateContainer, ageGateCheckbox } = this.getControls();
        if (!ageGateContainer || !ageGateCheckbox) {
            console.warn('Age gate controls not found');
            return;
        }

        const form = document.getElementById(formId);
        if (!form) {
            console.warn(`Form with ID ${formId} not found`);
            return;
        }

        const minorVerificationInput = document.createElement('input');

        minorVerificationInput.type = 'hidden';
        minorVerificationInput.name = 'IsMinor';
        minorVerificationInput.value = (
            !ageGateContainer.hidden && ageGateCheckbox.checked
        ).toString();

        form.appendChild(minorVerificationInput);
    },

    checkAgeGate: function () {
        const { ageGateContainer, ageGateCheckbox } = this.getControls();

        if (!this.isDOBValid) {
            return;
        }

        const dobMonth = document.getElementById('dobMonth').value;
        const dobDay = document.getElementById('dobDay').value;
        const dobYear = document.getElementById('dobYear').value;

        if (!dobMonth || !dobDay || !dobYear) {
            return;
        }

        const birthDate = new Date(`${dobMonth}/${dobDay}/${dobYear}`);
        const today = new Date();

        // Calculate age
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        // Adjust age if birthday hasn't occurred yet this year
        if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        // Check if DoB under 18
        this.isUnderAge = age < 18;

        // Show/hide age gate based on age
        if (this.isUnderAge) {
            ageGateContainer.hidden = false;
            // Uncheck the box when it appears to force user action
            ageGateCheckbox.checked = false;
        } else {
            ageGateContainer.hidden = true;
            // Checkbox is not visible, so it doesn't need to be checked
            ageGateCheckbox.checked = false;
        }

        this.validateAllInputs();
    },

    validateAllInputs: function () {
        const {
            registrationMemberIdInput,
            registrationSsnLastFourInput,
            registrationEmailInput,
            registrationConfirmEmailInput,
            registrationPhoneInput,
            registrationTermsCheckbox,
            registrationSubmitButton,
            ageGateContainer,
            ageGateCheckbox
        } = this.getControls();

        // Check if age gate is needed and checked
        const ageGateRequired = this.isUnderAge && !ageGateContainer.hidden;
        const ageGateValid = !ageGateRequired || ageGateCheckbox.checked;

        // A sponsor's form renders either Member ID or the SSN, card and ZIP set.
        const identityValid = registrationSsnLastFourInput
            ? ssnIdentityFields.every(([inputId, , , isValid]) =>
                  isValid(document.getElementById(inputId).value)
              )
            : isValidMemberId(registrationMemberIdInput.value);

        if (
            identityValid &&
            isValidEmail(registrationEmailInput.value) &&
            isEmailsMatch(
                registrationEmailInput.value,
                registrationConfirmEmailInput.value
            ) &&
            this.isDOBValid &&
            isValidPhoneNumber(registrationPhoneInput.value) &&
            registrationTermsCheckbox.checked &&
            ageGateValid
        ) {
            registrationSubmitButton.disabled = false;
        } else {
            registrationSubmitButton.disabled = true;
        }
    },

    validateMemberId: function () {
        const {
            registrationMemberIdInput,
            memberIdMsgIdRequired,
            memberIdMsgIdInvalid
        } = this.getControls();

        if (registrationMemberIdInput.value.length === 0) {
            registrationMemberIdInput.classList.add('is-invalid');
            memberIdMsgIdInvalid.setAttribute('hidden', true);
            memberIdMsgIdRequired.removeAttribute('hidden');
        } else if (isValidMemberId(registrationMemberIdInput.value)) {
            registrationMemberIdInput.classList.remove('is-invalid');
            memberIdMsgIdRequired.setAttribute('hidden', true);
            memberIdMsgIdInvalid.setAttribute('hidden', true);
        } else {
            registrationMemberIdInput.classList.add('is-invalid');
            memberIdMsgIdRequired.setAttribute('hidden', true);
            memberIdMsgIdInvalid.removeAttribute('hidden');
        }
    },

    validateEmail: function () {
        const {
            registrationEmailInput,
            registrationConfirmEmailInput,
            emailRequired,
            emailInvalid,
            confirmationEmailGroup
        } = this.getControls();

        if (isValidEmail(registrationEmailInput.value)) {
            registrationEmailInput.classList.remove('is-invalid');
            this.isEmailValid = true;
            emailRequired.setAttribute('hidden', true);
            emailInvalid.setAttribute('hidden', true);
        } else if (registrationEmailInput.value.length === 0) {
            registrationEmailInput.classList.add('is-invalid');
            emailInvalid.setAttribute('hidden', true);
            emailRequired.removeAttribute('hidden');
        } else {
            registrationEmailInput.classList.add('is-invalid');
            emailRequired.setAttribute('hidden', true);
            emailInvalid.removeAttribute('hidden');
        }

        if (this.isEmailValid) {
            confirmationEmailGroup.classList.remove('-disabled');
            registrationConfirmEmailInput.disabled = false;
        }
    },

    validateTwoEmailsMatch: function () {
        const {
            registrationEmailInput,
            registrationConfirmEmailInput,
            emailsDoNotMatch
        } = this.getControls();

        if (
            isEmailsMatch(
                registrationEmailInput.value,
                registrationConfirmEmailInput.value
            )
        ) {
            registrationConfirmEmailInput.classList.remove('is-invalid');
            registrationConfirmEmailInput.classList.add('is-valid');
            emailsDoNotMatch.setAttribute('hidden', true);
        } else {
            registrationConfirmEmailInput.classList.remove('is-valid');
            registrationConfirmEmailInput.classList.add('is-invalid');
            emailsDoNotMatch.removeAttribute('hidden');
        }
    },

    validatePhoneNumber: function () {
        const { registrationPhoneInput, phoneInvalid } = this.getControls();

        if (isValidPhoneNumber(registrationPhoneInput.value)) {
            registrationPhoneInput.classList.remove('is-invalid');
            phoneInvalid.setAttribute('hidden', true);
        } else {
            registrationPhoneInput.classList.add('is-invalid');
            phoneInvalid.removeAttribute('hidden');
        }
    }
};

const ssoEmailState = {
    isEmailValid: false,

    getControls: function () {
        const ssoEmailInput = document.getElementById('ssoEmailInput');

        return { ssoEmailInput };
    },

    init: function () {
        const { ssoEmailInput } = this.getControls();

        ssoEmailInput.addEventListener('input', event => {
            this.validateEmail();
            this.validateAllInputs();
        });
    },

    validateAllInputs: function () {
        const { ssoEmailInput } = this.getControls();

        if (isValidEmail(ssoEmailInput.value)) {
            ssoEmailSubmitButton.disabled = false;
        } else {
            ssoEmailSubmitButton.disabled = true;
        }
    },

    validateEmail: function () {
        const { ssoEmailInput } = this.getControls();

        if (isValidEmail(ssoEmailInput.value)) {
            ssoEmailInput.classList.remove('is-invalid');
            this.isEmailValid = true;
            emailRequired.setAttribute('hidden', true);
            emailInvalid.setAttribute('hidden', true);
        } else if (ssoEmailInput.value.length === 0) {
            ssoEmailInput.classList.add('is-invalid');
            emailRequired.setAttribute('hidden', true);
            emailInvalid.removeAttribute('hidden', true);
        } else {
            ssoEmailInput.classList.add('is-invalid');
            this.isEmailValid = false;
        }
    }
};

const preferenceState = {
    accountPreferenceInitial: false,
    promotionalPreferenceInitial: false,

    getControls: function () {
        const accountNotificationToggle = document.getElementById(
            'accountNotificationToggle'
        );
        const preferencePhoneInput = document.getElementById(
            'preferencePhoneInput'
        );
        const preferencePhoneInvalid = document.getElementById(
            'preferencePhoneInvalid'
        );
        const preferencePhoneRequired = document.getElementById(
            'preferencePhoneRequired'
        );
        const promotionalMessageToggle = document.getElementById(
            'promotionalMessageToggle'
        );
        const updatePreferencesButton = document.getElementById(
            'updatePreferencesButton'
        );
        return {
            accountNotificationToggle,
            preferencePhoneInput,
            preferencePhoneInvalid,
            preferencePhoneRequired,
            promotionalMessageToggle,
            updatePreferencesButton
        };
    },

    init: function () {
        this.initStatus();

        // we don't want to do it more than once
        window.initStatus = this.initStatus.bind(this);

        htmx.on('htmx:afterRequest', event => {
            const { preferencePhoneInput, updatePreferencesButton } =
                this.getControls();
            if (!preferencePhoneInput) {
                updatePreferencesButton.style.visibility = 'hidden';
            } else {
                updatePreferencesButton.style.visibility = 'visible';
            }
        });
    },
    initStatus: function () {
        const {
            accountNotificationToggle,
            preferencePhoneInput,
            promotionalMessageToggle,
            updatePreferencesButton
        } = this.getControls();
        this.accountPreferenceInitial = accountNotificationToggle.checked;
        this.promotionalPreferenceInitial = promotionalMessageToggle.checked;
        accountNotificationToggle.addEventListener('click', event => {
            this.validateUpdateButton();
            this.validationSMSPrefHasPhone();
        });
        promotionalMessageToggle.addEventListener('click', event => {
            this.validateUpdateButton();
            this.validationSMSPrefHasPhone();
        });
        preferencePhoneInput.addEventListener('input', event => {
            this.validatePreferencePhone();
            this.validationSMSPrefHasPhone();
            this.validateUpdateButton();
        });
        updatePreferencesButton.disabled = true;
    },
    validatePreferencePhone: function () {
        const { preferencePhoneInput, preferencePhoneInvalid } =
            this.getControls();

        // Regular expression to match 10 digits or digits separated by hyphens
        const regex = /^(|\d{10}|\d{3}-\d{3}-\d{4})$/;

        // Check if the input matches the format
        if (!regex.test(preferencePhoneInput.value)) {
            preferencePhoneInput.classList.add('is-invalid');
            preferencePhoneInvalid.removeAttribute('hidden');
        } else {
            preferencePhoneInput.classList.remove('is-invalid');
            preferencePhoneInvalid.setAttribute('hidden', true);
        }
    },
    validateUpdateButton: function () {
        const { preferencePhoneInput, updatePreferencesButton } =
            this.getControls();

        // if
        // 1. phone number is invalid
        // 2. phone number is null + toggle is updated + phone number is invalid || phone number length is 0
        // 3. no toggle is updated && no phone number is updated
        // then disable the button
        if (
            !this.isPhoneInputValid() ||
            (this.isToggleUpdated() &&
                this.isPhoneNull() &&
                (!this.isPhoneInputValid() ||
                    preferencePhoneInput.value.length == 0)) ||
            (!this.isToggleUpdated() && preferencePhoneInput.value.length == 0)
        ) {
            updatePreferencesButton.disabled = true;
        } else {
            updatePreferencesButton.disabled = false;
        }
    },
    validationSMSPrefHasPhone: function () {
        const {
            accountNotificationToggle,
            preferencePhoneInput,
            preferencePhoneRequired,
            promotionalMessageToggle
        } = this.getControls();

        if (
            (accountNotificationToggle.checked ||
                promotionalMessageToggle.checked) &&
            preferencePhoneInput.placeholder.length == 0 &&
            preferencePhoneInput.value.length == 0
        ) {
            preferencePhoneInput.classList.add('is-invalid');
            preferencePhoneRequired.removeAttribute('hidden');
            return false;
        } else {
            preferencePhoneInput.classList.remove('is-invalid');
            preferencePhoneRequired.setAttribute('hidden', true);
            return true;
        }
    },
    isPhoneInputValid: function () {
        const { preferencePhoneInput } = this.getControls();
        const regex = /^(|\d{10}|\d{3}-\d{3}-\d{4})$/;
        return regex.test(preferencePhoneInput.value);
    },

    isPhoneNull: function () {
        const { preferencePhoneInput } = this.getControls();
        return preferencePhoneInput.placeholder.length == 0;
    },

    isToggleUpdated: function () {
        const { accountNotificationToggle, promotionalMessageToggle } =
            this.getControls();

        if (
            this.accountPreferenceInitial !=
                accountNotificationToggle.checked ||
            this.promotionalPreferenceInitial !=
                promotionalMessageToggle.checked
        ) {
            return true;
        } else {
            return false;
        }
    }
};

const cardActivateState = {
    hasPINFields: function () {
        return document.getElementById('cardPINInput') !== null;
    },
    getPublicKey: function () {
        var step2 = document.getElementById('activate-step2');
        return step2 ? step2.getAttribute('data-pin-public-key') : null;
    },
    getPINValidationContext: function () {
        var form = document.querySelector('.mcp-card-activation-form');
        if (!window.pinValidation || !form) {
            return {};
        }
        return window.pinValidation.readContextFromForm(form);
    },
    init: function () {
        var self = this;
        var cardLastFourDigitsInput = document.getElementById(
            'cardLastFourDigitsInput'
        );
        var cardExpirationInput = document.getElementById(
            'cardExpirationInput'
        );

        cardLastFourDigitsInput.addEventListener('input', function () {
            self.validateCardLastFourDigits();
            self.validateStep1();
            // In the E6-only single-step form the Activate button is gated on
            // last4 + exp + PIN together, so re-evaluate step 2 as last4 changes.
            if (self.hasPINFields()) self.validateStep2();
        });
        cardExpirationInput.addEventListener('input', function (event) {
            self.formatExpDate(event);
            self.validateExpirationDate();
            self.validateStep1();
            if (self.hasPINFields()) self.validateStep2();
        });

        document.addEventListener('htmx:afterSettle', function (event) {
            if (
                event.detail.target &&
                event.detail.target.id === 'activate-step2'
            ) {
                self.initStep2();
                // Re-gate the swapped-in button. The error/activate partials
                // render their button enabled by default (no Disabled prop), so
                // without re-running the gating an invalid re-submit is possible
                // until the user edits an input again.
                self.validateStep1();
                if (self.hasPINFields()) self.validateStep2();
            }
        });

        // E6-only members get the PIN fields rendered inline at initial load
        // (no HTMX swap fires for them), so wire up step 2 now.
        if (this.hasPINFields()) {
            this.initStep2();
        }
    },
    validateStep1: function () {
        var cardLastFourDigitsInput = document.getElementById(
            'cardLastFourDigitsInput'
        );
        var cardExpirationInput = document.getElementById(
            'cardExpirationInput'
        );
        // Prefer the lookup button (two-step flow). When the form is rendered
        // in single-step mode for non-E6-only members, the same Last4+Exp
        // gating drives the direct Activate button instead. The PIN-fields
        // check keeps validateStep1 from clobbering validateStep2's gating
        // after the two-step E6 swap, where cardActivationButton exists but
        // should be PIN-gated, not Last4+Exp-gated.
        var stepButton = document.getElementById('cardLookupButton');
        if (!stepButton && !this.hasPINFields()) {
            stepButton = document.getElementById('cardActivationButton');
        }
        if (!stepButton) return;

        stepButton.disabled = !(
            isValidLastFourDigits(cardLastFourDigitsInput.value) &&
            isValidFutureExpirationDate(cardExpirationInput.value)
        );
    },
    initStep2: function () {
        if (!this.hasPINFields()) return;
        var self = this;

        var cardPINInput = document.getElementById('cardPINInput');
        var cardConfirmPINInput = document.getElementById(
            'cardConfirmPINInput'
        );
        var cardActivationButton = document.getElementById(
            'cardActivationButton'
        );

        if (!cardPINInput || !cardConfirmPINInput || !cardActivationButton)
            return;

        cardPINInput.addEventListener('input', function () {
            self.validatePIN();
            self.validateConfirmPIN();
            self.validateStep2();
        });
        cardConfirmPINInput.addEventListener('input', function () {
            self.validateConfirmPIN();
            self.validateStep2();
        });

        this.initPinEncryption(cardActivationButton, cardPINInput);
    },
    validateStep2: function () {
        var cardLastFourDigitsInput = document.getElementById(
            'cardLastFourDigitsInput'
        );
        var cardExpirationInput = document.getElementById(
            'cardExpirationInput'
        );
        var cardPINInput = document.getElementById('cardPINInput');
        var cardConfirmPINInput = document.getElementById(
            'cardConfirmPINInput'
        );
        var cardActivationButton = document.getElementById(
            'cardActivationButton'
        );
        if (!cardActivationButton) return;

        // Require last4 + exp in addition to a valid PIN. In the two-step flow
        // these are already filled (they drove the lookup); in the E6-only
        // single-step form they share the screen with the PIN fields, so the
        // Activate button must stay disabled until all four are valid.
        cardActivationButton.disabled = !(
            isValidLastFourDigits(cardLastFourDigitsInput.value) &&
            isValidFutureExpirationDate(cardExpirationInput.value) &&
            isValidPIN(cardPINInput.value) &&
            cardPINInput.value === cardConfirmPINInput.value
        );
    },
    initPinEncryption: function (submitButton, pinInput) {
        var publicKey = this.getPublicKey();

        // Skip encryption if no public key is configured or the encryption module failed to load.
        if (!publicKey || typeof window.encryptPIN !== 'function') {
            return;
        }

        // htmx:confirm fires before the request and supports async via issueRequest().
        submitButton.addEventListener('htmx:confirm', function (event) {
            var pin = pinInput.value.trim();
            if (!pin || !isValidPIN(pin)) {
                return;
            }

            // If encrypted data is already populated, this is a re-trigger from
            // issueRequest() below — let it through without re-encrypting.
            var hiddenPin = document.getElementById('encryptedPIN');
            if (hiddenPin && hiddenPin.value) {
                return;
            }

            // Pause the HTMX request while we encrypt asynchronously.
            event.preventDefault();
            submitButton.disabled = true;

            window
                .encryptPIN(pin, publicKey)
                .then(function (payload) {
                    // Populate hidden fields with encrypted values.
                    var hiddenSymKey = document.getElementById(
                        'encryptedSymmetricKey'
                    );
                    var hiddenChecksum = document.getElementById('pinChecksum');

                    if (hiddenSymKey)
                        hiddenSymKey.value = payload.encryptedSymmetricKey;
                    if (hiddenPin) hiddenPin.value = payload.encryptedPin;
                    if (hiddenChecksum) hiddenChecksum.value = payload.checksum;

                    // Strip plaintext PIN fields so they are not submitted.
                    pinInput.removeAttribute('name');
                    var confirmInput = document.getElementById(
                        'cardConfirmPINInput'
                    );
                    if (confirmInput) confirmInput.removeAttribute('name');

                    // Resume the original HTMX request with encrypted data.
                    event.detail.issueRequest();
                })
                .catch(function (err) {
                    console.error('PIN encryption failed:', err);
                    submitButton.disabled = false;
                });
        });
    },
    validateCardLastFourDigits: function () {
        var cardLastFourDigitsInput = document.getElementById(
            'cardLastFourDigitsInput'
        );
        var lastFourMsgInvalid = document.getElementById('lastFourMsgInvalid');
        var lastFourMsgRequired = document.getElementById(
            'lastFourMsgRequired'
        );

        if (cardLastFourDigitsInput.value.length === 0) {
            cardLastFourDigitsInput.classList.add('is-invalid');
            lastFourMsgInvalid.setAttribute('hidden', true);
            lastFourMsgRequired.removeAttribute('hidden');
        } else if (isValidLastFourDigits(cardLastFourDigitsInput.value)) {
            cardLastFourDigitsInput.classList.remove('is-invalid');
            lastFourMsgInvalid.setAttribute('hidden', true);
            lastFourMsgRequired.setAttribute('hidden', true);
        } else {
            cardLastFourDigitsInput.classList.add('is-invalid');
            lastFourMsgInvalid.removeAttribute('hidden');
            lastFourMsgRequired.setAttribute('hidden', true);
        }
    },
    validateExpirationDate: function () {
        var cardExpirationInput = document.getElementById(
            'cardExpirationInput'
        );
        var expirationMsgInvalid = document.getElementById(
            'expirationMsgInvalid'
        );
        var expirationMsgRequired = document.getElementById(
            'expirationMsgRequired'
        );

        if (cardExpirationInput.value.length === 0) {
            cardExpirationInput.classList.add('is-invalid');
            expirationMsgInvalid.setAttribute('hidden', true);
            expirationMsgRequired.removeAttribute('hidden');
        } else if (isValidFutureExpirationDate(cardExpirationInput.value)) {
            cardExpirationInput.classList.remove('is-invalid');
            expirationMsgInvalid.setAttribute('hidden', true);
            expirationMsgRequired.setAttribute('hidden', true);
        } else {
            cardExpirationInput.classList.add('is-invalid');
            expirationMsgInvalid.removeAttribute('hidden');
            expirationMsgRequired.setAttribute('hidden', true);
        }
    },
    hideAllPINRuleMessages: function () {
        var PINMsgInvalid = document.getElementById('PINMsgInvalid');
        var PINMsgRequired = document.getElementById('PINMsgRequired');
        var PINMsgAllSame = document.getElementById('PINMsgAllSame');
        var PINMsgSequential = document.getElementById('PINMsgSequential');
        if (PINMsgInvalid) PINMsgInvalid.setAttribute('hidden', true);
        if (PINMsgRequired) PINMsgRequired.setAttribute('hidden', true);
        if (PINMsgAllSame) PINMsgAllSame.setAttribute('hidden', true);
        if (PINMsgSequential) PINMsgSequential.setAttribute('hidden', true);
        var PINMsgDobPattern = document.getElementById('PINMsgDobPattern');
        var PINMsgLastFour = document.getElementById('PINMsgLastFour');
        var PINMsgPhone = document.getElementById('PINMsgPhone');
        if (PINMsgDobPattern) PINMsgDobPattern.setAttribute('hidden', true);
        if (PINMsgLastFour) PINMsgLastFour.setAttribute('hidden', true);
        if (PINMsgPhone) PINMsgPhone.setAttribute('hidden', true);
    },
    validatePIN: function () {
        if (!this.hasPINFields()) return;
        var cardPINInput = document.getElementById('cardPINInput');
        this.hideAllPINRuleMessages();

        if (cardPINInput.value.length === 0) {
            cardPINInput.classList.add('is-invalid');
            document.getElementById('PINMsgRequired').removeAttribute('hidden');
            return;
        }

        var result = window.pinValidation.validate(
            cardPINInput.value,
            this.getPINValidationContext()
        );
        if (result.valid) {
            cardPINInput.classList.remove('is-invalid');
            return;
        }

        cardPINInput.classList.add('is-invalid');
        if (result.errors.length > 0) {
            var match = document.getElementById('PINMsg' + result.errors[0]);
            if (match) {
                match.removeAttribute('hidden');
                return;
            }
        }
        document.getElementById('PINMsgInvalid').removeAttribute('hidden');
    },
    validateConfirmPIN: function () {
        if (!this.hasPINFields()) return;
        var cardPINInput = document.getElementById('cardPINInput');
        var cardConfirmPINInput = document.getElementById(
            'cardConfirmPINInput'
        );
        var confirmPINMsgMismatch = document.getElementById(
            'confirmPINMsgMismatch'
        );
        var confirmPINMsgRequired = document.getElementById(
            'confirmPINMsgRequired'
        );

        if (cardConfirmPINInput.value.length === 0) {
            cardConfirmPINInput.classList.add('is-invalid');
            confirmPINMsgMismatch.setAttribute('hidden', true);
            confirmPINMsgRequired.removeAttribute('hidden');
        } else if (cardConfirmPINInput.value !== cardPINInput.value) {
            cardConfirmPINInput.classList.add('is-invalid');
            confirmPINMsgRequired.setAttribute('hidden', true);
            confirmPINMsgMismatch.removeAttribute('hidden');
        } else {
            cardConfirmPINInput.classList.remove('is-invalid');
            confirmPINMsgMismatch.setAttribute('hidden', true);
            confirmPINMsgRequired.setAttribute('hidden', true);
        }
    },
    formatExpDate: function (event) {
        let input = event.target.value.replace(/\D/g, '');
        if (input.length > 2) {
            input = input.slice(0, 2) + '/' + input.slice(2);
        }
        event.target.value = input;
    }
};

export const sponsorlessLookUpFormState = {
    getControls: function () {
        const sponsorlessDOBPicker = document.getElementById('dobPicker');
        const sponsorlessForm = document.getElementById('sponsorlessForm');
        const sponsorlessZipcodeInput = document.getElementById(
            'sponsorlessZipcodeInput'
        );
        const sponsorlessZipcodeRequired = document.getElementById(
            'sponsorlessZipcodeRequired'
        );
        const sponsorlessZipcodeInvalid = document.getElementById(
            'sponsorlessZipcodeInvalid'
        );
        const sponsorlessSubmitButton = document.getElementById(
            'sponsorlessSubmitButton'
        );

        return {
            sponsorlessDOBPicker,
            sponsorlessForm,
            sponsorlessZipcodeInput,
            sponsorlessZipcodeRequired,
            sponsorlessZipcodeInvalid,
            sponsorlessSubmitButton
        };
    },
    init: function () {
        const {
            sponsorlessDOBPicker,
            sponsorlessForm,
            sponsorlessSubmitButton,
            sponsorlessZipcodeInput
        } = this.getControls();

        // Once per form element: a tie swaps in a fresh form whose inputs need
        // binding, and afterSettle also fires for unrelated swaps like modals.
        if (sponsorlessForm.dataset.validationBound) return;
        sponsorlessForm.dataset.validationBound = 'true';

        sponsorlessDOBPicker.addEventListener(
            DobPicker.ValidityChangeEventName,
            this.updateSubmitButtonDisabledState.bind(this)
        );

        sponsorlessZipcodeInput.addEventListener('input', event => {
            this.validateZipcode();
            this.updateSubmitButtonDisabledState();
        });

        // Present only on the tie prompt.
        const cardLastFourInput = document.getElementById(
            'sponsorlessCardLastFourInput'
        );
        if (cardLastFourInput) {
            cardLastFourInput.addEventListener('input', () => {
                this.validateCardLastFour();
                this.updateSubmitButtonDisabledState();
            });
        }

        // A tie re-render arrives with Next enabled and DOB and ZIP filled in.
        this.updateSubmitButtonDisabledState();
    },
    updateSubmitButtonDisabledState() {
        const { sponsorlessForm, sponsorlessSubmitButton } = this.getControls();
        sponsorlessSubmitButton.disabled = !sponsorlessForm.checkValidity();
    },
    validateZipcode: function () {
        const {
            sponsorlessZipcodeInput,
            sponsorlessZipcodeRequired,
            sponsorlessZipcodeInvalid
        } = this.getControls();

        if (!isValidZipcode(sponsorlessZipcodeInput.value)) {
            sponsorlessZipcodeInput.setCustomValidity('invalid');
            sponsorlessZipcodeInput.classList.add('is-invalid');
            sponsorlessZipcodeRequired.setAttribute('hidden', true);
            sponsorlessZipcodeInvalid.removeAttribute('hidden');
        } else if (!sponsorlessZipcodeInput.value) {
            sponsorlessZipcodeInput.setCustomValidity('invalid');
            sponsorlessZipcodeInput.classList.add('is-invalid');
            sponsorlessZipcodeRequired.removeAttribute('hidden');
            sponsorlessZipcodeInvalid.setAttribute('hidden', true);
        } else {
            sponsorlessZipcodeInput.setCustomValidity('');
            sponsorlessZipcodeInput.classList.remove('is-invalid');
            sponsorlessZipcodeRequired.setAttribute('hidden', true);
            sponsorlessZipcodeInvalid.setAttribute('hidden', true);
        }
    },
    validateCardLastFour: function () {
        showFieldFeedback(
            document.getElementById('sponsorlessCardLastFourInput'),
            document.getElementById('sponsorlessCardLastFourRequired'),
            document.getElementById('sponsorlessCardLastFourInvalid'),
            isValidLastFourDigits
        );
    }
};

function isValidMemberId(memberId) {
    return memberId.length > 0;
}

function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isEmailsMatch(email1, email2) {
    return email1 === email2;
}

function isValidPhoneNumber(phone) {
    return phone.length === 10 || phone.length === 0;
}

// Shows the required or invalid message for an input, or neither. An empty
// input is only an error when it is required.
export function showFieldFeedback(input, requiredMsg, invalidMsg, isValid) {
    const empty = input.value.length === 0;
    const missing = empty && input.required;
    const malformed = !empty && !isValid(input.value);
    input.classList.toggle('is-invalid', missing || malformed);
    requiredMsg.toggleAttribute('hidden', !missing);
    invalidMsg.toggleAttribute('hidden', !malformed);
}

// The member lookup matches five digits against a stored ZIP or ZIP+4, but a
// typed ZIP+4 misses a stored five-digit ZIP.
export function isFiveDigitZipcode(zipcode) {
    return /^\d{5}$/.test(zipcode);
}

// Accept standard 5 digit zipcode or 5+4 digit zipcode
function isValidZipcode(zipcode) {
    // Accepts: "12345", "12345-1234", or "123451234"
    return /^\d{5}(-?\d{4})?$/.test(zipcode);
}

function isValidLastFourDigits(cardLastFourDigits) {
    return /^\d{4}$/.test(cardLastFourDigits);
}

function isValidPIN(pin) {
    var form = document.querySelector('.mcp-card-activation-form');
    var ctx =
        window.pinValidation && form
            ? window.pinValidation.readContextFromForm(form)
            : {};
    return window.pinValidation.isValid(pin, ctx);
}

function isValidFutureExpirationDate(date) {
    // Check if the date is in MM/YY format
    const regex = /^(0[1-9]|1[0-2])\/\d{2}$/;
    if (!regex.test(date)) return false;

    // Split the input into month and 2-digit year, then expand to a 4-digit year.
    const [month, shortYear] = date.split('/').map(Number);

    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // JS months are 0-indexed
    const currentYear = currentDate.getFullYear();
    const year = Math.floor(currentYear / 100) * 100 + shortYear;

    if (year > currentYear) {
        return true;
    } else if (year === currentYear && month >= currentMonth) {
        return true;
    }

    return false;
}

window.addEventListener('load', function () {
    if (document.getElementById('registrationForm')) {
        registrationState.init();

        // Add toggler button handling
        const toggler = document.querySelector(
            '.mcp-button.-registration-toggler'
        );
        if (toggler) {
            toggler.addEventListener('click', function () {
                // Bootstrap will toggle aria-expanded automatically
                // We just need to add/remove the -toggled class based on that state
                setTimeout(() => {
                    if (toggler.getAttribute('aria-expanded') === 'true') {
                        toggler.classList.add('-toggled');
                    } else {
                        toggler.classList.remove('-toggled');
                    }
                }, 10);
            });
        }
    }

    if (document.getElementById('ssoEmailForm')) {
        ssoEmailState.init();
    }

    if (document.getElementById('preferences-form')) {
        preferenceState.init();
    }

    if (document.getElementById('activate-card-main')) {
        cardActivateState.init();
    }
    if (document.getElementById('sponsorlessLookUpForm')) {
        sponsorlessLookUpFormState.init();
    }
});

document.addEventListener('htmx:afterSettle', () => {
    if (document.getElementById('registrationForm')) {
        registrationState.init();
    }
    if (document.getElementById('sponsorlessLookUpForm')) {
        sponsorlessLookUpFormState.init();
    }
});

window.isValidZipcode = isValidZipcode;
